import { Component, OnInit, Input, EventEmitter, Output, AfterViewInit, signal, computed, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CookieService } from 'ngx-cookie-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ConfigSchema, ExtraContentAttachment, ExtraContentType } from '../../../models/config-schema.model';
import { DocumentLegalValueCombinationDescription, Papel } from '../../../models/edocs.model';
import { FlowObjectDefinition, FlowObjectInstance, FlowObjectInstanceState, FlowObjectType } from '../../../models/flow-object.model';
import { FlowDefinition, FlowInstance, FlowInstanceState } from '../../../models/flow.model';
import { PdfUploadValue } from '../../../models/helper-classes.model';
import { InputDataTaskFinishContent, InputDataTaskForm, OutputDataTaskFinishContent } from '../../../models/input-output-data.model';
import { AuthService } from '../../../services/auth.service';
import { EDocsService } from '../../../services/edocs.service';
import { Enums } from '../../../shared/enums';
import { Utils } from '../../../shared/utils';
import { PdfPreviewDialogComponent } from '../../pdf-preview-dialog/pdf-preview-dialog.component';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fob-finish-content',
    templateUrl: './fob-finish-content.component.html',
    styleUrl: './fob-finish-content.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class FlowObjectFinishContentComponent implements OnInit, OnChanges, AfterViewInit {
    // #region [Type props]
    DocumentLegalValueCombinationDescription = DocumentLegalValueCombinationDescription;
    FlowObjectInstanceState = FlowObjectInstanceState;
    FlowObjectType = FlowObjectType;
    Utils = Utils;
    Enums = Enums;
    // #endregion

    // #region [props]
    outputData: OutputDataTaskFinishContent;
    forwardingProtocol = Enums.Messages.DefaultEmptyValue as string;
    processProtocol = Enums.Messages.DefaultEmptyValue as string;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $flowInstance = signal(null as FlowInstance);
    $inputData = signal(null as InputDataTaskFinishContent);
    $configSchema = signal(null as ConfigSchema);
    $flowObjectDefinition = signal(null as FlowObjectDefinition);
    $publicMessageHtml = signal(null as string);
    $waitingMessage = signal(null as string);
    $papeisSelector = signal([] as Papel[]);
    $currentDocuments = signal([] as any[]);
    $forgotPapel = signal(false);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isPublicMessageHtmlLoading = computed(() => Utils.isNullOrEmpty(this.$publicMessageHtml()));
    $isWaitingMessageLoading = computed(() => Utils.isNullOrEmpty(this.$waitingMessage()));
    $hasPendingTask = computed(() => this.$model()?.stateId == FlowObjectInstanceState.Started);
    $isReadOnly = computed(() => [FlowObjectInstanceState.Finished, FlowObjectInstanceState.NotStarted].includes(this.$model()?.stateId) || this.$flowInstance()?.stateId === FlowInstanceState.CancelledByDeliberation);
    $isMissingMessage = computed(() => Utils.isNullOrEmpty(this.$inputData()?.message) || this.$inputData().message.trim().length < 5);
    $isMissingAttachments = computed(() => this.$inputData().attachments?.length != this.$configSchema()?.taskFinishContent?.extraContent?.attachments?.length);
    $isTextContentType = computed(() => this.$configSchema()?.taskFinishContent?.extraContent?.contentType == ExtraContentType.Text);
    $isPlural = computed(() => this.$configSchema()?.taskFinishContent?.extraContent?.attachments?.length > 1);
    $dateString = computed(() => this.$model() == null ? '' : new Date(this.$model().updateDate).toLocaleString());
    $shouldLetPublicAgentInteract = computed(() =>
        this.$currentUser()?.isPublicAgent
        && this.$model()?.flowObjectInstanceActors.some(x => x.actorId == this.$currentUser()?.id)
        && (
            this.$flowInstance()?.ownerId != this.$currentUser()?.id
            || this.$currentUser()?.papeis?.some(x =>
                x.isCurrentUserHeadOfThisRolesOrganization
                && x.lotacaoGuid.trim().toLowerCase() == this.$flowInstance()?.currentActionableLocationId.trim().toLowerCase()
            )
        )
    );
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Input() _flowInstance: FlowInstance;
    @Input() _flowDefinition: FlowDefinition;
    @Output() _submitEvent = new EventEmitter<FlowObjectInstance>();
    // #endregion

    constructor(
        private dialog: MatDialog,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private authService: AuthService,
        private cookieService: CookieService,
        private eDocsService: EDocsService
    ) { }

    async ngOnInit() {
        Utils.setSignal(this.$flowObjectDefinition, this._flowDefinition.flowObjectDefinitions.find(x => x.id == this.$model().flowObjectDefinitionId));
        Utils.setSignal(this.$configSchema, JSON.parse(this.$flowObjectDefinition().configSchema) as ConfigSchema);
        Utils.setSignal(this.$inputData, Object.assign(new InputDataTaskFinishContent(), JSON.parse(this.$model().inputData)));
        this.outputData = this.$model().outputData == null ? null : Object.assign(new OutputDataTaskFinishContent(), JSON.parse(this.$model().outputData));

        // #region [protocolo de Encaminhamento]
        if (Utils.isNullOrEmpty(this.$inputData().eDocsData.forwardingId)) {
            this.toastr.error(Enums.Messages.ForwardingProtocolMissingError, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        const response_Encaminhamento = await this.eDocsService.getEncaminhamento(this.$inputData().eDocsData.forwardingId);

        if (!response_Encaminhamento.isSuccess) {
            this.toastr.error(response_Encaminhamento.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        const encaminhamento = response_Encaminhamento.data;
        this.forwardingProtocol = encaminhamento.protocolo;
        // #endregion

        // #region [protocolo de Processo]
        if (Utils.isNotNullOrEmpty(this.$inputData().eDocsData.processId)) {
            const response_Processo = await this.eDocsService.getProcesso(this.$inputData().eDocsData.processId);

            if (!response_Processo.isSuccess) {
                this.toastr.error(response_Processo.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.processProtocol = response_Processo.data.protocolo;
        }
        // #endregion

        // #region [documentos capturados na Tarefa corrente]
        if (Utils.isNotNullOrEmpty(this.outputData)) {
            let filtered = encaminhamento.documentosEncaminhamento.filter(x =>
                x.documento.encaminhamentoId == this.outputData.eDocsData.forwardingId
                && !x.controle
            );

            for (let item of filtered) {
                Utils.updSignal(this.$currentDocuments, x => {
                    if(!item.documento.nome.endsWith('Cancelamento Manual')) {
                        x.push({
                            id: item.documento.id,
                            registro: item.documento.registro,
                            filename: `${item.documento.nome}.${item.documento.extensao}`
                        });
                        return x;
                    }
                });
            }
        }
        // #endregion

        // #region [seletor de papéis]
        const response_UsuarioPapeisEncaminhamento = await this.eDocsService.getUsuarioPapeisEncaminhamento(this.$inputData().eDocsData.forwardingId);

        if (!response_UsuarioPapeisEncaminhamento.isSuccess) {
            this.toastr.error(response_UsuarioPapeisEncaminhamento.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        Utils.setSignal(this.$papeisSelector, response_UsuarioPapeisEncaminhamento.data);

        // força o usuário a selecionar um papel manualmente
        setTimeout(() => Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = null;
            return x;
        }), 50);
        // #endregion

        // #region [mensagem pública customizada]
        if (Utils.isNotNullOrEmpty(this.$flowObjectDefinition()?.publicMessageHtml)) {
            setTimeout(() => this.$publicMessageHtml.set(Utils.getParsedMessageHtml(
                this.cookieService,
                this.$flowObjectDefinition().publicMessageHtml,
                this.forwardingProtocol,
                this.processProtocol
            )), 1000);
        }
        // #endregion

        // #region [mensagem de espera do requerente]
        let bufferMessage = this.resolveMessageFormTags(this.$configSchema()?.taskFinishContent?.extraContent?.waitingMessage);

        if (Utils.isNotNullOrEmpty(bufferMessage)) {
            setTimeout(() => this.$waitingMessage.set(Utils.getParsedMessageHtml(
                this.cookieService,
                bufferMessage,
                this.forwardingProtocol,
                this.processProtocol
            )), 1000);
        }
        // #endregion

        // #region [mensagem do conteúdo]
        if (this.$isTextContentType()) {
            Utils.updSignal(this.$inputData, x => {
                x.message = this.resolveMessageFormTags(this.$configSchema()?.taskFinishContent?.extraContent?.message);
                return x;
            });
        }
        // #endregion
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowInstance.set(this._flowInstance);
    }

    ngAfterViewInit() {
        if (this.$hasPendingTask() && this.$inputData().eDocsData.signerId == null) {
            Utils.updSignal(this.$inputData, x => {
                x.eDocsData.signerId = this.$currentUser().papeis.find(x => x.prioritario).guid;
                return x;
            });
        }
    }

    async openDocumentModal(id: string) {
        this.spinner.show('flowInstance');

        const response = await this.eDocsService.getDocumentoDownloadUrl(id);

        this.spinner.hide('flowInstance');

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        setTimeout(() => {
            this.dialog.open(PdfPreviewDialogComponent, {
                data: {
                    content: response.data
                }
            });
        }, 200);
    }

    onPdfUploadChange(event: PdfUploadValue, attachment: ExtraContentAttachment) {
        let buffer = (this.$inputData().attachments || []).filter(x => x.id != attachment.id);
        buffer.push({
            id: attachment.id,
            identificadorTemporarioArquivoNaNuvem: event.minioKey[0]
        });
        Utils.updSignal(this.$inputData, x => {
            x.attachments = buffer;
            return x;
        });
    }

    getDocumentLegalValueCombination(item: ExtraContentAttachment): string {
        return this.DocumentLegalValueCombinationDescription.get(+item.documentLegalValueCombination).split(':')[1].trim();
    }

    getLegalReasoningsNames(item: ExtraContentAttachment): string {
        return item.accessLevel.legalReasoningsNames.map(x => x.split(' - ')[1].trim().toLocaleUpperCase()).join('; ');
    }

    signerIdChange(event) {
        Utils.updSignal(this.$inputData, x => {
            x.eDocsData.signerId = event;
            return x;
        });
    }

    onChangeMessage(event) {
        Utils.updSignal(this.$inputData, x => {
            x.message = event;
            return x;
        });
    }

    submit() {
        if (this.$isTextContentType() && this.$isMissingMessage()) {
            this.toastr.warning(Enums.Messages.MessageMinlengthError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return;
        }

        if (!this.$isTextContentType() && this.$isMissingAttachments()) {
            this.toastr.warning(Enums.Messages.MissingAttachmentsError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return;
        }

        if (Utils.isNullOrEmpty(this.$inputData().eDocsData.signerId)) {
            this.toastr.warning(Enums.Messages.NoSignerIdError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            this.$forgotPapel.set(true);
            return;
        }

        Utils.updSignal(this.$model, x => {
            x.inputData = JSON.stringify(this.$inputData());
            return x;
        });

        this._submitEvent.emit(this.$model());
    }

    // #region [===== private methods =====]
    // #endregion

    private resolveMessageFormTags(message: string): string {
        if (Utils.isNullOrEmpty(message) == null) return '';

        let formData = this.getFormData();
        if (formData == null) return '';

        let bufferMessage = message;
        let tags = bufferMessage.match(/\{\|([\w]+)\|\}/g);
        if (tags?.length > 0) {
            let formFields = tags.map(x => x.replace(/[^\w]/g, ''));

            for (let i = 0; i < tags.length; i++) {
                bufferMessage = bufferMessage.replaceAll(
                    tags[i],
                    formData[formFields[i]] || ''
                ).replace(/ +/g, ' ').trim();
            }
        }

        return bufferMessage;
    }

    private getFormData(): any {
        let formTaskDefinition = this._flowDefinition.flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
        if (formTaskDefinition == null) return null;

        let formTaskInstance = this.$flowInstance().flowObjectInstances.find(x => x.flowObjectDefinitionId == formTaskDefinition.id);
        let formData = JSON.parse(formTaskInstance.inputData) as InputDataTaskForm;

        return formData.data;
    }
}
